import React, { useState, useEffect } from "react";
import CustomDropdown, { DropdownOption } from "./CustomDropdown";
import { useTranslation } from "../i18n";
import "./NumberPad.css";
import { Delivery, Room } from "../services/types";

interface NumberPadProps {
  type: "delivery" | "room"; // 改为 "room" 而不是 "door"
  onClose: () => void;
  onSubmit: (code: string, selectedOption?: string) => void;
  title: string;
  deliveries?: Delivery[];
  rooms?: Room[];
}

const NumberPad: React.FC<NumberPadProps> = ({
  type,
  onClose,
  onSubmit,
  title,
  deliveries = [],
  rooms = [],
}) => {
  const [code, setCode] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [showError, setShowError] = useState(false);
  const { t } = useTranslation();

  // 转换真实数据为下拉选项格式
  const deliveryOptions: DropdownOption[] = deliveries.map((delivery) => ({
    id: delivery.id,
    name: delivery.name,
  }));

  const roomOptions: DropdownOption[] = rooms.map((room) => ({
    id: room.id,
    name: room.name,
    floor: room.floorName ? parseInt(room.floorName) || 0 : 0,
  }));

  // 根据类型获取选项
  const options = type === "delivery" ? deliveryOptions : roomOptions;

  // 键盘按键配置
  const keys = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    [t.numberPad.clear, "0", t.numberPad.confirm],
  ];

  // 处理按键点击
  const handleKeyPress = (key: string) => {
    if (key === t.numberPad.clear || key === "Clear") {
      setCode("");
      setShowError(false);
    } else if (key === t.numberPad.confirm || key === "Confirm") {
      if (code.length >= 4) {
        onSubmit(code, selectedOption);
      } else {
        setShowError(true);
        setTimeout(() => setShowError(false), 2000);
      }
    } else {
      if (code.length < 6) {
        // 限制最大长度
        setCode((prev) => prev + key);
        setShowError(false);
      }
    }
  };

  // 处理键盘事件
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key;

      if (key >= "0" && key <= "9") {
        handleKeyPress(key);
      } else if (key === "Enter") {
        handleKeyPress("Confirm");
      } else if (key === "Backspace" || key === "Delete") {
        handleKeyPress("Clear");
      } else if (key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [code]);

  // 阻止背景滚动
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <div className="numberpad-overlay" onClick={onClose}>
      <div className="numberpad-container" onClick={(e) => e.stopPropagation()}>
        {/* 头部 */}
        <div className="numberpad-header">
          <h2 className="numberpad-title">
            {type === "delivery"
              ? t.numberPad.deliveryTitle
              : t.numberPad.roomTitle}
          </h2>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* 下拉选择框 */}
        <div className="dropdown-section">
          <CustomDropdown
            options={options}
            value={selectedOption}
            onChange={setSelectedOption}
            label={
              type === "delivery"
                ? t.numberPad.selectDeliveryService
                : t.numberPad.selectRoom
            }
            placeholder={t.numberPad.placeholder}
            searchable={true}
          />
        </div>

        {/* 密码显示区域 */}
        <div className="code-display">
          <div className="code-dots">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className={`code-dot ${index < code.length ? "filled" : ""}`}
              />
            ))}
          </div>
          {showError && (
            <div className="error-message">{t.numberPad.errorMinDigits}</div>
          )}
        </div>

        {/* 数字键盘 */}
        <div className="numberpad-grid">
          {keys.map((row, rowIndex) => (
            <div key={rowIndex} className="numberpad-row">
              {row.map((key) => (
                <button
                  key={key}
                  className={`numberpad-key ${
                    key === t.numberPad.confirm || key === "Confirm"
                      ? "confirm-key"
                      : key === t.numberPad.clear || key === "Clear"
                      ? "clear-key"
                      : "number-key"
                  }`}
                  onClick={() => handleKeyPress(key)}
                >
                  {key}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* 提示信息 */}
        <div className="numberpad-hint">
          <p>
            {type === "delivery"
              ? t.numberPad.hintDelivery
              : t.numberPad.hintRoom}
          </p>
          <p>{t.numberPad.hintMinMax}</p>
        </div>
      </div>
    </div>
  );
};

export default NumberPad;
